import { useCallback, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { useSnackbar } from 'notistack';
import {
  Box,
  Card,
  CardContent,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import useSubscribeStorage from 'src/hooks/useSubscribeStorage';
import { DEFAULT_ENVIRONMENT_ID } from 'src/constants/storage';
import { ENVIRONMENT_NAME_QUERY_KEY } from 'src/constants/query';
import ApplicationsService from 'src/services/applications.service';
import { pagesText } from 'src/constants/pagesText';
import IconButton from 'src/components/IconButton';
import Iconify from 'src/components/Iconify';
import { parseEnvironmentId } from 'src/utils/environments.util';
import { IdField } from 'src/pages/company/environments/constants';
import DeviceLoggingApiKeyDialog from 'src/pages/company/environments/DeviceLogginApiKeyDialog';
import ConfirmDeviceKeyGenerationDialog from 'src/pages/company/environments/ConfirmDeviceKeyGenerationDialog';
import useDialog from 'src/hooks/useDialog';
import useRegenerateKey from 'src/queries/enviornment/useRegenerateKey';
import useUpdateEnvironments from 'src/queries/enviornment/useUpdateEnvironments';

const StyledCard = styled(Card)(({ theme }) => ({
  backgroundColor: '#4B4B4B',
  borderRadius: theme.spacing(1),
  marginBottom: theme.spacing(3),
}));

const StyledCardHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: theme.spacing(2),
  borderBottom: '1px solid #5B5B5B',
}));

const StyledCardTitle = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.pxToRem(18),
  fontWeight: 600,
  color: '#fff',
}));

const StyledCardContent = styled(CardContent)(({ theme }) => ({
  padding: theme.spacing(2),
}));

const InfoRow = styled(TableRow)({
  '& td': {
    borderBottom: '1px solid #5B5B5B',
    padding: '12px 16px',
  },
  '&:last-child td': {
    borderBottom: 'none',
  },
});

const InfoLabel = styled(Typography)({
  fontSize: '14px',
  color: '#DDDDDD',
  fontWeight: 500,
});

const InfoValue = styled(Typography)({
  fontSize: '14px',
  color: '#fff',
  fontWeight: 400,
  wordBreak: 'break-word',
});

const EditableFieldCell = styled(TableCell)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
});

const EnvironmentSummary = () => {
  const customerEnvId = useSubscribeStorage(DEFAULT_ENVIRONMENT_ID);
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const regnerateKeyMutation = useRegenerateKey();

  const [deviceLoggingApiKey, setDeviceLoggingApiKey] = useState('');
  const [editingField, setEditingField] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [editValues, setEditValues] = useState({
    name: '',
    description: '',
    location: '',
  });

  const updateEnvironmentMutation = useUpdateEnvironments({ mode: 'edit' });

  const { data: environment, isLoading } = useQuery(
    [ENVIRONMENT_NAME_QUERY_KEY, customerEnvId],
    () => ApplicationsService.getEnvironment(customerEnvId),
    { enabled: !!customerEnvId, refetchOnWindowFocus: false },
  );

  const {
    open: isDeviceLoggingApiKeyDialogOpen,
    onOpen: onOpenDeviceLoggingApiKeyDialog,
    onClose: onCloseDeviceLoggingApiKeyDialog,
  } = useDialog();

  const {
    open: isConfirmDeviceKeyGenerationDialogOpen,
    onOpen: onOpenConfirmDeviceKeyGenerationDialog,
    onClose: onCloseConfirmDeviceKeyGenerationDialog,
  } = useDialog();

  const managementSolutionLabel = useMemo(() => {
    if (!environment) return '-';

    const solutionType =
      environment.applicationManagementSolution ||
      parseEnvironmentId(environment.configManagerSupportId || '').type;

    switch (solutionType) {
      case IdField.CONFIG_MANAGER:
        return pagesText.environments.envIdMenu.configurationManager;
      case IdField.INTUNE:
        return pagesText.environments.envIdMenu.intune;
      case IdField.OTHER:
        return pagesText.environments.envIdMenu.other;
      default:
        return '-';
    }
  }, [environment]);

  const handleStartEdit = useCallback(
    field => {
      if (!environment) return;
      setEditingField(field);
      setEditValues({
        name: environment.name || '',
        description: environment.description || '',
        location: environment.location || '',
      });
    },
    [environment],
  );

  const handleCancelEdit = useCallback(() => {
    setEditingField(null);
    setIsSaving(false);
    setEditValues({ name: '', description: '', location: '' });
  }, []);

  const handleSaveField = useCallback(
    field => {
      if (isSaving || !environment) return;

      const value = editValues[field];
      const currentValue = environment[field] || '';

      if (value === currentValue) {
        handleCancelEdit();
        return;
      }

      setIsSaving(true);
      const solutionType =
        environment.applicationManagementSolution ||
        parseEnvironmentId(environment.configManagerSupportId || '').type;

      updateEnvironmentMutation.mutate(
        {
          id: customerEnvId,
          name: field === 'name' ? value : environment.name,
          description: field === 'description' ? value : environment.description,
          location: field === 'location' ? value : environment.location,
          configManagerSupportId: environment.configManagerSupportId,
          applicationManagementSolution: solutionType,
        },
        {
          onSuccess: () => {
            queryClient.invalidateQueries([ENVIRONMENT_NAME_QUERY_KEY, customerEnvId]);
            setEditingField(null);
            setIsSaving(false);
          },
          onError: error => {
            setIsSaving(false);
            enqueueSnackbar(error?.message || pagesText.somethingWentWrong, {
              variant: 'error',
              autoHideDuration: 3000,
            });
          },
        },
      );
    },
    [
      customerEnvId,
      editValues,
      enqueueSnackbar,
      environment,
      handleCancelEdit,
      isSaving,
      queryClient,
      updateEnvironmentMutation,
    ],
  );

  const handleKeyPress = useCallback(
    (e, field) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSaveField(field);
      } else if (e.key === 'Escape') {
        handleCancelEdit();
      }
    },
    [handleSaveField, handleCancelEdit],
  );

  const regenerateKey = useCallback(() => {
    regnerateKeyMutation.mutate(
      { activeEnvironmentId: customerEnvId },
      {
        onSuccess: newKey => {
          enqueueSnackbar(pagesText.environments.deviceLoggingApiKey.regenerateSuccess, {
            variant: 'success',
            autoHideDuration: 3000,
          });
          onCloseConfirmDeviceKeyGenerationDialog();
          onCloseDeviceLoggingApiKeyDialog();
          setDeviceLoggingApiKey(newKey);
          setTimeout(() => {
            onOpenDeviceLoggingApiKeyDialog();
          }, 100);
        },
        onError: error => {
          enqueueSnackbar(error?.message || pagesText.somethingWentWrong, {
            variant: 'error',
            autoHideDuration: 3000,
          });
          onCloseConfirmDeviceKeyGenerationDialog();
        },
      },
    );
  }, [
    regnerateKeyMutation,
    customerEnvId,
    enqueueSnackbar,
    onCloseConfirmDeviceKeyGenerationDialog,
    onCloseDeviceLoggingApiKeyDialog,
    onOpenDeviceLoggingApiKeyDialog,
  ]);

  const onDeviceLoggingApiKeySuccess = useCallback(() => {
    if (deviceLoggingApiKey) {
      onOpenConfirmDeviceKeyGenerationDialog();
    } else {
      regenerateKey();
    }
  }, [deviceLoggingApiKey, onOpenConfirmDeviceKeyGenerationDialog, regenerateKey]);

  const handleDeviceLoggingKeyClick = useCallback(() => {
    setDeviceLoggingApiKey(environment?.deviceLoggingApiKey || '');
    onOpenDeviceLoggingApiKeyDialog();
  }, [environment?.deviceLoggingApiKey, onOpenDeviceLoggingApiKeyDialog]);

  const renderEditableField = (field, label) => {
    if (!environment) return null;

    const isEditing = editingField === field;
    const value = isEditing ? editValues[field] : environment[field] || '-';

    return (
      <EditableFieldCell>
        {isEditing ? (
          <Stack direction="row" alignItems="center" spacing={1} sx={{ width: '100%' }}>
            <TextField
              value={editValues[field] || ''}
              onChange={e => setEditValues(prev => ({ ...prev, [field]: e.target.value }))}
              onKeyDown={e => handleKeyPress(e, field)}
              onBlur={() => {
                if (!isSaving) {
                  handleSaveField(field);
                }
              }}
              autoFocus
              size="small"
              multiline={field === 'description'}
              minRows={field === 'description' ? 2 : 1}
              disabled={isSaving}
              sx={{
                flexGrow: 1,
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#333',
                  color: '#fff',
                  fontSize: '14px',
                  '& fieldset': {
                    borderColor: '#5B5B5B',
                  },
                  '&:hover fieldset': {
                    borderColor: '#7B7B7B',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#7DFF92',
                  },
                },
              }}
            />
            <IconButton
              size="small"
              onMouseDown={e => e.preventDefault()}
              onClick={() => handleSaveField(field)}
              title={`Save ${label}`}>
              <Iconify icon="ic:round-check" width={18} height={18} color="#7DFF92" />
            </IconButton>
            <IconButton
              size="small"
              onMouseDown={e => e.preventDefault()}
              onClick={handleCancelEdit}
              title={`Cancel ${label}`}>
              <Iconify icon="ic:round-close" width={18} height={18} color="#a2a2a2" />
            </IconButton>
          </Stack>
        ) : (
          <>
            <InfoValue>{value}</InfoValue>
            <IconButton size="small" onClick={() => handleStartEdit(field)} title={`Edit ${label}`}>
              <Iconify icon="ic:baseline-edit" width={18} height={18} color="#a2a2a2" />
            </IconButton>
          </>
        )}
      </EditableFieldCell>
    );
  };

  if (isLoading || !environment) {
    return <Box>Loading...</Box>;
  }

  return (
    <Box>
      <StyledCard>
        <StyledCardHeader>
          <StyledCardTitle>{pagesText.environment.detailsCard.title}</StyledCardTitle>
        </StyledCardHeader>
        <StyledCardContent>
          <Table>
            <TableBody>
              <InfoRow>
                <TableCell>
                  <InfoLabel>Name:</InfoLabel>
                </TableCell>
                {renderEditableField('name', 'Name')}
              </InfoRow>
              <InfoRow>
                <TableCell>
                  <InfoLabel>Description:</InfoLabel>
                </TableCell>
                {renderEditableField('description', 'Description')}
              </InfoRow>
              <InfoRow>
                <TableCell>
                  <InfoLabel>Location:</InfoLabel>
                </TableCell>
                {renderEditableField('location', 'Location')}
              </InfoRow>
              <InfoRow>
                <TableCell>
                  <InfoLabel>{pagesText.environments.applicationManagementPlaceholder}:</InfoLabel>
                </TableCell>
                <TableCell>
                  <InfoValue>{managementSolutionLabel}</InfoValue>
                </TableCell>
              </InfoRow>
              <InfoRow>
                <TableCell>
                  <InfoLabel>Identifier/Code:</InfoLabel>
                </TableCell>
                <TableCell>
                  <InfoValue>{environment?.configManagerSupportId || '-'}</InfoValue>
                </TableCell>
              </InfoRow>
              <InfoRow>
                <TableCell>
                  <InfoLabel>{pagesText.environment.detailsCard.deviceLoggingKey}:</InfoLabel>
                </TableCell>
                <TableCell>
                  <InfoValue
                    onClick={handleDeviceLoggingKeyClick}
                    sx={{
                      cursor: 'pointer',
                      '&:hover': {
                        textDecoration: 'underline',
                      },
                    }}>
                    {environment?.deviceLoggingApiKey ? '****************' : 'Not set'}
                  </InfoValue>
                </TableCell>
              </InfoRow>
            </TableBody>
          </Table>
        </StyledCardContent>
      </StyledCard>

      <DeviceLoggingApiKeyDialog
        isOpen={isDeviceLoggingApiKeyDialogOpen}
        onClose={onCloseDeviceLoggingApiKeyDialog}
        onSuccess={onDeviceLoggingApiKeySuccess}
        apiKey={deviceLoggingApiKey}
      />

      <ConfirmDeviceKeyGenerationDialog
        isOpen={isConfirmDeviceKeyGenerationDialogOpen}
        onClose={onCloseConfirmDeviceKeyGenerationDialog}
        onSuccess={regenerateKey}
      />
    </Box>
  );
};

export default EnvironmentSummary;
